import { useState } from "react";
import { motion } from "framer-motion";
import { Package, Heart, ShieldCheck, Truck } from "lucide-react";
import { toast } from "sonner";
import { Page, FadeUp, MaskedLines } from "@/components/Reveal";

const PERKS = [
    { icon: Package, title: "Order Tracking", copy: "Follow every URWAN piece from atelier to doorstep." },
    { icon: Heart, title: "Wishlist", copy: "Save the pieces you're waiting on for the next drop." },
    { icon: ShieldCheck, title: "Warranty Registration", copy: "Activate your lifetime stitch & tear cover in a tap." },
    { icon: Truck, title: "Free Shipping", copy: "Complimentary shipping on every order, across India." },
];

export default function Account() {
    const [tab, setTab] = useState("login");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");

    const submit = (e) => {
        e.preventDefault();
        if (!email.trim() || !password.trim()) {
            toast.error("Please fill in both fields");
            return;
        }
        if (tab === "login") toast.success("Welcome back — signed in (demo experience).");
        else toast.success("Account created — welcome to URWAN (demo experience).");
        setEmail("");
        setPassword("");
    };

    const input =
        "w-full border border-white/15 bg-coal px-4 py-3.5 text-sm text-ink placeholder:text-mute/50 focus:border-gold focus:outline-none transition-colors";
    const label = "mb-2 block text-[10px] font-mono uppercase tracking-[0.25em] text-mute";

    return (
        <Page className="pb-28">
            <section className="mx-auto w-full max-w-[1400px] px-5 pb-14 pt-36 md:px-8 md:pt-44 lg:px-10">
                <p className="flex items-center gap-3 text-[11px] font-mono uppercase tracking-[0.3em] text-gold">
                    <span className="inline-block h-px w-10 bg-gold" /> My Account
                </p>
                <h1 className="mt-5 font-display text-5xl font-extrabold uppercase leading-[0.95] tracking-tight sm:text-6xl">
                    <MaskedLines lines={["Welcome to", "Urwan."]} />
                </h1>
            </section>

            <section className="mx-auto grid w-full max-w-[1000px] gap-10 px-5 md:px-8 lg:grid-cols-2 lg:items-stretch">
                <FadeUp>
                    <div className="border border-white/10 bg-coal/60" data-testid="account-panel">
                        <div className="flex border-b border-white/10" data-testid="account-tabs">
                            {[
                                ["login", "Login"],
                                ["register", "Register"],
                            ].map(([id, lbl]) => (
                                <button
                                    key={id}
                                    data-testid={`account-${id}-tab`}
                                    onClick={() => setTab(id)}
                                    className={`relative flex-1 py-5 font-mono text-xs uppercase tracking-[0.2em] transition-colors ${
                                        tab === id ? "text-gold" : "text-mute hover:text-ink"
                                    }`}
                                >
                                    {lbl}
                                    {tab === id && (
                                        <motion.span
                                            layoutId="account-tab-underline"
                                            className="absolute inset-x-0 bottom-0 h-[2px] bg-gold"
                                            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                                        />
                                    )}
                                </button>
                            ))}
                        </div>
                        <form onSubmit={submit} className="space-y-6 p-8 md:p-10">
                            <div>
                                <label className={label} htmlFor="a-email">
                                    {tab === "login" ? "Username or email address *" : "Email address *"}
                                </label>
                                <input
                                    id="a-email"
                                    data-testid="account-email"
                                    className={input}
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    placeholder="you@example.com"
                                />
                            </div>
                            <div>
                                <label className={label} htmlFor="a-pass">Password *</label>
                                <input
                                    id="a-pass"
                                    data-testid="account-password"
                                    type="password"
                                    className={input}
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    placeholder="••••••••"
                                />
                            </div>
                            {tab === "login" && (
                                <label className="flex items-center gap-3 text-sm text-mute">
                                    <input type="checkbox" data-testid="account-remember" className="h-4 w-4 accent-[#D4AF37]" />
                                    Remember me
                                </label>
                            )}
                            <button
                                type="submit"
                                data-testid={`account-${tab}-submit`}
                                className="w-full bg-gold py-4 font-display text-sm font-bold uppercase tracking-[0.2em] text-black transition-colors hover:bg-ink"
                            >
                                {tab === "login" ? "Log In" : "Register"}
                            </button>
                            {tab === "login" && (
                                <p className="text-center text-xs text-mute">
                                    Lost your password?{" "}
                                    <a href="mailto:info@urwan.com" className="text-gold hover:underline">
                                        Contact us
                                    </a>
                                </p>
                            )}
                            <p className="text-center text-[11px] leading-relaxed text-mute/70">
                                Demo experience for the new URWAN site — no account data is stored.
                            </p>
                        </form>
                    </div>
                </FadeUp>

                <div className="grid content-start gap-5">
                    {PERKS.map((p, i) => (
                        <FadeUp key={p.title} delay={i * 0.08}>
                            <div className="group flex items-start gap-5 border border-white/10 bg-coal/60 p-6 transition-colors duration-300 hover:border-gold/50">
                                <span className="grid h-11 w-11 shrink-0 place-items-center rounded-full border border-gold/40 text-gold transition-colors group-hover:bg-gold group-hover:text-black">
                                    <p.icon size={17} />
                                </span>
                                <div>
                                    <h3 className="font-display text-base font-bold uppercase text-ink">{p.title}</h3>
                                    <p className="mt-1 text-sm leading-relaxed text-mute">{p.copy}</p>
                                </div>
                            </div>
                        </FadeUp>
                    ))}
                </div>
            </section>
        </Page>
    );
}
